import { mapToDeliveryAddress } from './DeliveryAddress';

export interface AutocompleteSuggestion {
  placePrediction: {
    placeId: string;
    structuredFormat: {
      mainText: {
        text: string;
      };
      secondaryText: {
        text: string;
      };
    };
    text: {
      text: string;
    };
  };
}

export interface PlacesDetails {
  addressComponents: [
    {
      longText: string;
      shortText: string;
      types: string[];
      languageCode: string;
    },
  ];
  formattedAddress: string;
  location: {
    latitude: number;
    longitude: number;
  };
  postalAddress: {
    administrativeArea: string;
    languageCode: string;
    postalCode: string;
    locality: string;
    regionCode: string;
    addressLines: string[];
  };
}

interface AutocompleteRequestBody {
  input: string;
  includedRegionCodes?: string;
  locationBias?: {
    circle?: {
      center: PositionCoordinates;
      radius: number;
    };
  };
}

const API_KEY_ERROR = 'API key is required for Google Places or Geocoding';
const API_RESPONSE_ERROR =
  'Error fetching data from the Google Places or Geocoding API';
const PLACE_ID_ERROR = 'Place ID is required for Google Places';
const COUNTRY_CODE_ERROR = 'Country code is required for Google Places';
const LAT_LONG_ERROR =
  'Latitude and Longitude are required for Google Geocoding';

const autocompleteApiUrl =
  'https://places.googleapis.com/v1/places:autocomplete';
const placeDetailsApiUrl = 'https://places.googleapis.com/v1/places';

const ADDRESS_INPUT_MINIMUM_LENGTH = 5;
const AUTOCOMPLETE_LOCATION_BIAS_RADIUS_METERS = 500;

export const getAddressSuggestionsAsync = async (
  fetchFn: typeof fetch,
  apiKey: string,
  input: string,
  resultsType: 'simple' | 'detailed',
  countryCode: string,
  userLocation?: PositionCoordinates,
): Promise<DeliveryAddress[]> => {
  if (!apiKey) {
    throw new Error(API_KEY_ERROR);
  }

  if (!countryCode) {
    throw new Error(COUNTRY_CODE_ERROR);
  }

  if (!input || input.length < ADDRESS_INPUT_MINIMUM_LENGTH) {
    return [];
  }

  const requestBody: AutocompleteRequestBody = {
    input,
    includedRegionCodes: countryCode,
  };

  if (userLocation) {
    requestBody.locationBias = {
      circle: {
        center: {
          latitude: userLocation.latitude,
          longitude: userLocation.longitude,
        },
        radius: AUTOCOMPLETE_LOCATION_BIAS_RADIUS_METERS,
      },
    };
  }

  const requestBodyJson = JSON.stringify(requestBody);

  const response = await fetchFn(autocompleteApiUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Goog-Api-Key': apiKey,
    },
    body: requestBodyJson,
  });

  if (!response.ok) {
    throw new Error(`${API_RESPONSE_ERROR}: ${response.statusText}`);
  }

  const data = await response.json();

  if (!data.suggestions || !Array.isArray(data.suggestions)) {
    throw new Error(API_RESPONSE_ERROR);
  }

  const suggestions: AutocompleteSuggestion[] = data.suggestions;

  let addresses: DeliveryAddress[];

  if (resultsType === 'simple') {
    addresses = suggestions.map((suggestion) => {
      return mapToDeliveryAddress(
        suggestion.placePrediction.placeId,
        suggestion,
      );
    });
  } else {
    const placeIds = suggestions.map(
      (suggestion: AutocompleteSuggestion) =>
        suggestion.placePrediction.placeId,
    );
    addresses = await Promise.all(
      placeIds.map(async (placeId: string) => {
        const address = await getDeliveryAddressFromPlaceIdAsync(
          fetchFn,
          apiKey,
          placeId,
        );
        return address;
      }),
    );
  }

  return addresses;
};

export const getGeocodingDetailsAsync = async (
  fetchFn: typeof fetch,
  apiKey: string,
  latitude: number,
  longitude: number,
): Promise<DeliveryAddress> => {
  if (!apiKey) {
    throw new Error(API_KEY_ERROR);
  }

  if (latitude == null || longitude == null) {
    throw new Error(LAT_LONG_ERROR);
  }

  const response = await fetchFn(
    `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${apiKey}`,
  );

  if (!response.ok) {
    throw new Error(`${API_RESPONSE_ERROR}: ${response.statusText}`);
  }

  let data = await response.json();

  if (!data.results || !Array.isArray(data.results)) {
    throw new Error(API_RESPONSE_ERROR);
  }

  const placeId = data.results[0].place_id;

  if (!placeId) {
    throw new Error(PLACE_ID_ERROR);
  }

  const address = await getDeliveryAddressFromPlaceIdAsync(
    fetchFn,
    apiKey,
    placeId,
  );

  return address;
};

export const getDeliveryAddressFromPlaceIdAsync = async (
  fetchFn: typeof fetch,
  apiKey: string,
  placeId: string,
): Promise<DeliveryAddress> => {
  if (!apiKey) {
    throw new Error(API_KEY_ERROR);
  }

  if (!placeId) {
    throw new Error(PLACE_ID_ERROR);
  }

  const placeDetails = await getPlaceDetailsAsync(fetchFn, apiKey, placeId);

  const address = mapToDeliveryAddress(placeId, placeDetails);

  return address;
};

const getPlaceDetailsAsync = async (
  fetchFn: typeof fetch,
  apiKey: string,
  placeId: string,
): Promise<PlacesDetails> => {
  const fieldMasks = [
    'location',
    'formattedAddress',
    'addressComponents',
    'postalAddress',
  ];

  const response = await fetchFn(`${placeDetailsApiUrl}/${placeId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-Goog-Api-Key': apiKey,
      'X-Goog-FieldMask': fieldMasks.join(','),
    },
  });

  if (!response.ok) {
    throw new Error(`${API_RESPONSE_ERROR}: ${response.statusText}`);
  }

  const data: PlacesDetails = await response.json();

  return data;
};
